import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useAdminViewAs } from "@/context/AdminViewAsContext";
import type { Database } from "@/integrations/supabase/types";

type CustomerStage = Database["public"]["Enums"]["customer_stage"];

export const useCustomerStage = () => {
  const { user } = useAuth();
  const { viewAsUser, isAdminViewMode } = useAdminViewAs();
  const [stage, setStage] = useState<CustomerStage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStage = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      const targetUserId = isAdminViewMode && viewAsUser ? viewAsUser.id : user.id;

      try {
        const { data, error: fetchError } = await supabase
          .from("customer_stages")
          .select("current_stage")
          .eq("user_id", targetUserId)
          .single();

        if (fetchError) {
          // No row found — treat as signed_up (default)
          if (fetchError.code === "PGRST116") {
            setStage("signed_up");
          } else {
            console.error("Error fetching customer stage:", fetchError);
            setError(fetchError.message);
          }
        } else {
          setStage(data.current_stage as CustomerStage);
        }
      } catch (err) {
        console.error("Error fetching customer stage:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchStage();
  }, [user, viewAsUser, isAdminViewMode]);

  return { stage, loading, error };
};
